<template>
    <div id="sidebar-primary" class="sidebar p-2 border-gray border-b lg:min-h-screen lg:border-r lg:border-b-0 lg:p-5 lg:fixed bg-white lg:h-full">
        <div class="flex justify-between items-center lg:flex-col lg:items-start h-full relative">
            <!-- Hamburger -->
            <div class="absolute items-center lg:hidden">
                <button @click="showingNavigationDropdown = ! showingNavigationDropdown" class="inline-flex items-center justify-center p-2 rounded-md text-gray-darker hover:bg-gray-light focus:outline-none focus:bg-gray-light transition">
                    <svg class="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                        <path :class="{'hidden': showingNavigationDropdown, 'inline-flex': ! showingNavigationDropdown }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                        <path :class="{'hidden': ! showingNavigationDropdown, 'inline-flex': showingNavigationDropdown }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <jet-application-mark class="block h-10 w-auto mx-auto lg:mb-5 lg:h-24" />

            <!-- Select customer dropdown -->
            <customer-selector class="w-full hidden lg:block" />

            <nav class="h-full hidden lg:block lg:mt-5">
                <ul>
                    <li v-if="!onlyLocationRole && !isOnlyPicker">
                        <jet-nav-link :href="route('dashboard')" :active="route().current('dashboard')">Dashboard</jet-nav-link>
                    </li>
                    <li v-if="!onlyLocationRole && !isOnlyPicker">
                        <jet-nav-link :href="route('catalogue')" :active="route().current('catalogue')">Catalogue</jet-nav-link>
                    </li>
                    <li v-if="isAdmin || isLocation">
                        <jet-nav-link :href="route('orders.index')" :active="route().current().indexOf('orders') !== -1">Orders</jet-nav-link>
                    </li>
                    <li v-if="isCustomer">
                        <jet-nav-link :href="route('customer_orders.index')" :active="route().current().indexOf('customer_orders') !== -1">Orders</jet-nav-link>
                    </li>
                    <li v-if="isPicker">
                        <jet-nav-link :href="route('picking.index')" :active="route().current().indexOf('picking') !== -1">Picking</jet-nav-link>
                    </li>
                    <li v-if="isAdmin || hasRole('picker')">
                        <jet-nav-link :href="route('loads.index')" :active="route().current().indexOf('loads') !== -1">Loads</jet-nav-link>
                    </li>
                    <li v-if="isAdmin">
                        <jet-nav-link :href="route('refunds.index')" :active="route().current().indexOf('refunds') !== -1">Credit Requests</jet-nav-link>
                    </li>
                    <li v-if="isCustomer">
                        <jet-nav-link :href="route('customer_refunds.index')" :active="route().current().indexOf('customer_refunds') !== -1">Credit Requests</jet-nav-link>
                    </li>
                    <li v-if="isAdmin">
                        <jet-nav-link :href="route('products.index')" :active="route().current().indexOf('products') !== -1">Products</jet-nav-link>
                    </li>
                    <li v-if="isAdmin && hasFinanceRole">
                        <jet-nav-link :href="route('pricing.index')" :active="route().current().indexOf('pricing') !== -1">Pricing</jet-nav-link>
                    </li>
                    <li v-if="isAdmin">
                        <jet-nav-link :href="route('customers.index')" :active="route().current().indexOf('customers') !== -1">Customers</jet-nav-link>
                    </li>
                    <li v-if="isAdmin">
                        <jet-nav-link :href="route('announcements.index')" :active="route().current().indexOf('announcements') !== -1">Announcements</jet-nav-link>
                    </li>
                    <li v-if="isAdmin">
                        <jet-nav-link :href="route('sops.index')" :active="route().current().indexOf('sops') !== -1">SOPs</jet-nav-link>
                    </li>
                    <li v-if="isAdmin && hasReportsRole">
                        <jet-nav-link :href="route('reports.retail.index')" :active="route().current().indexOf('reports') !== -1">Reports</jet-nav-link>
                    </li>
                    <li v-if="isAdmin">
                        <jet-nav-link :href="route('settings.holidays.index')" :active="route().current().indexOf('settings') !== -1">Settings</jet-nav-link>
                    </li>
                    <li v-if="isCustomer">
                        <jet-nav-link :href="route('activecustomer.profile')" :active="route().current().indexOf('activecustomer') !== -1">Profile</jet-nav-link>
                    </li>
                </ul>
            </nav>

            <div class="hidden lg:flex lg:items-center">
                <!-- Settings Dropdown -->
                <div class="relative">
                    <jet-dropdown align="bottom" width="48">
                        <template #trigger>
                            <span class="inline-flex rounded-md">
                                <button type="button" class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-gray-500 bg-white hover:text-gray-700 focus:outline-none transition">
                                    {{ $page.props.user.name }}

                                    <svg class="ml-2 -mr-0.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                                    </svg>
                                </button>
                            </span>
                        </template>

                        <template #content>
                            <!-- Account Management -->
                            <div class="block px-4 py-2 text-xs text-gray-400">
                                Manage Account
                            </div>

                            <jet-dropdown-link :href="route('profile.show')">
                                Profile
                            </jet-dropdown-link>

                            <div class="border-t border-gray-100"></div>

                            <!-- Authentication -->
                            <form @submit.prevent="logout">
                                <jet-dropdown-link as="button">
                                    Log Out
                                </jet-dropdown-link>
                            </form>
                        </template>
                    </jet-dropdown>
                </div>
            </div>
        </div>

        <!-- Responsive Navigation Menu -->
        <div :class="{'block': showingNavigationDropdown, 'hidden': ! showingNavigationDropdown}" class="lg:hidden">
            <div class="pt-2 pb-3">
                <jet-responsive-nav-link v-if="!onlyLocationRole && !isOnlyPicker" :href="route('dashboard')" :active="route().current('dashboard')">
                    Dashboard
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="!onlyLocationRole && !isOnlyPicker" :href="route('catalogue')" :active="route().current('catalogue')">
                    Catalogue
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isAdmin || isLocation" :href="route('orders.index')" :active="route().current().indexOf('orders') !== -1">
                    Orders
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isCustomer" :href="route('customer_orders.index')" :active="route().current().indexOf('customer_orders') !== -1">
                    Orders
                </jet-responsive-nav-link>

                <jet-responsive-nav-link  v-if="isPicker" :href="route('picking.index')" :active="route().current().indexOf('picking') !== -1">
                    Picking
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isAdmin || isPicker" :href="route('loads.index')" :active="route().current().indexOf('loads') !== -1">
                    Loads
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isAdmin" :href="route('refunds.index')" :active="route().current().indexOf('refund') !== -1">
                    Credit Requests
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isAdmin" :href="route('customer_refunds.index')" :active="route().current().indexOf('customer_refunds') !== -1">
                    Credit Requests
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isAdmin" :href="route('products.index')" :active="route().current().indexOf('products') !== -1">
                    Products
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isAdmin && hasFinanceRole" :href="route('pricing.index')" :active="route().current().indexOf('pricing') !== -1">
                    Pricing
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isAdmin" :href="route('customers.index')" :active="route().current().indexOf('customers') !== -1">
                    Customers
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isAdmin" :href="route('announcements.index')" :active="route().current().indexOf('announcements') !== -1">
                    Announcements
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isAdmin" :href="route('sops.index')" :active="route().current().indexOf('sops') !== -1">
                    SOPs
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isAdmin && hasReportsRole" :href="route('reports.retail.index')" :active="route().current().indexOf('reports') !== -1">
                    Reports
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isAdmin" :href="route('settings.holidays.index')" :active="route().current().indexOf('settings') !== -1">
                    Settings
                </jet-responsive-nav-link>

                <jet-responsive-nav-link v-if="isCustomer" :href="route('activecustomer.profile')" :active="route().current().indexOf('activecustomer') !== -1">
                    Profile
                </jet-responsive-nav-link>
            </div>

            <div class="pt-4 pb-1 border-t border-gray-200 text-sm">
                <div class="flex items-center px-4">
                    <div>
                        <div class="font-medium text-sm text-gray-500">{{ $page.props.user.name }}</div>
                        <div class="font-medium text-xs text-gray-500">{{ $page.props.user.email }}</div>
                    </div>
                </div>

                <div class="mt-2">
                    <jet-responsive-nav-link :href="route('profile.show')" :active="route().current('profile.show')" class="text-sm">
                        Profile
                    </jet-responsive-nav-link>

                    <form method="POST" @submit.prevent="logout">
                        <jet-responsive-nav-link as="button" class="text-sm">
                            Log Out
                        </jet-responsive-nav-link>
                    </form>
                </div>
            </div>

            <div class="mt-2 mb-1">
                <label class="text-sm">Active Profile:</label>
                <customer-selector class="w-full lg:hidden" />
            </div>
        </div>

        <jet-confirmation-modal :show="showCustomerChangeModal" @close="showCustomerChangeModal = false">
            <template #title>
                Change Customer
            </template>

            <template #content>
                Unsaved changes exist, are you sure you want to change customers?
            </template>

            <template #footer>
                <jet-danger-button @click="handleActiveCustomerChange">
                    Yes
                </jet-danger-button>

                <jet-tertiary-button class="ml-2" @click="showCustomerChangeModal = false">
                    No
                </jet-tertiary-button>
            </template>
        </jet-confirmation-modal>

    </div>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3'
import UseUserAuth from '@/Composables/UseUserAuth';
import JetApplicationMark from '@/Jetstream/ApplicationMark.vue'
import JetDropdownLink from '@/Jetstream/DropdownLink'
import JetDropdown from '@/Jetstream/Dropdown'
import JetNavLink from '@/Jetstream/NavLink';
import JetResponsiveNavLink from '@/Jetstream/ResponsiveNavLink';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetConfirmationModal from '@/Jetstream/ConfirmationModal';
import CustomerSelector from '@/Shared/CustomerSelector';
import CartWidgetMobile from '@/Pages/Catalogue/Partials/CartWidgetMobile';

export default {
    components: {
        JetApplicationMark,
        JetDropdownLink,
        JetDropdown,
        JetNavLink,
        JetResponsiveNavLink,
        JetDangerButton,
        JetTertiaryButton,
        JetConfirmationModal,
        CustomerSelector,
        CartWidgetMobile,
    },

    setup () {
        const { isAdmin, isCustomer, isLocation, isPicker, isOnlyPicker, hasFinanceRole, hasReportsRole, hasRole } = UseUserAuth(usePage().props.value.user);

        return {
            isAdmin,
            isCustomer,
            isLocation,
            isPicker,
            isOnlyPicker,
            hasFinanceRole,
            hasReportsRole,
            hasRole
        }
    },

    data() {
        return {
            showCustomerChangeModal: false,
            showingNavigationDropdown: false,
        }
    },

    computed: {
        onlyLocationRole() {
            return this.isLocation && usePage().props.value.user.roles.length === 1;
        }
    },

    methods: {
        logout() {
            this.$inertia.post(route('logout'));
        },
    },
}
</script>

<style scoped>
</style>